import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Image } from 'react-native'
import { PaypalPNG } from '../../../utils/meldd-icons'
import { PaymentIcon } from './payment-channel.modal'
import { useCreatePaypalOrderMutation } from '../../../generated/graphql'
import { PaypalButtonsComponent } from './paypal-buttons.component'
import { getPaypalReturnUrls } from './paypal.utils'
import {
  isPaymentChannelEnabled,
  PaymentType,
} from './payment-visibility.registry'

type PaypalChannelProps = {
  billingId: string
  amount: number
  onProcess: () => void
}

/**
 * PayPal payment channel component
 *
 * Payment Flow:
 * 1. User clicks PayPal button and approves payment in PayPal dialog
 * 2. PayPal captures the payment
 * 3. PayPal sends webhook to backend callback endpoint
 * 4. Backend processes the webhook and updates billing status
 *
 * IMPORTANT - Webhook Configuration:
 * - PayPal webhooks are configured in PayPal Dashboard
 * - Backend endpoint: POST /payment-callback/paypal/:billingId
 * - The billingId is passed via the order's custom_id field so PayPal includes it in the webhook
 */
export const PaypalChannel = ({
  billingId,
  amount,
  onProcess,
}: PaypalChannelProps) => {
  const { t: tTiers } = useTranslation('tiers')
  const { t: tPayment } = useTranslation('payment')
  const [loading, setLoading] = useState(false)
  const [showPaypalModal, setShowPaypalModal] = useState(false)
  const [createPaypalOrderMutation] = useCreatePaypalOrderMutation()

  if (!isPaymentChannelEnabled(PaymentType.PAYPAL)) {
    return null
  }

  const handlePaypalClick = () => {
    setShowPaypalModal(true)
  }

  const handleClose = () => {
    setShowPaypalModal(false)
    setLoading(false)
  }

  const createPaypalOrder = async (): Promise<string> => {
    setLoading(true)
    try {
      const { returnUrl, cancelUrl } = getPaypalReturnUrls()

      const { data } = await createPaypalOrderMutation({
        variables: {
          input: {
            amount: amount.toFixed(2),
            currency: 'USD',
            description: 'Node tier payment',
            customId: billingId,
            returnUrl,
            cancelUrl,
          },
        },
      })

      if (!data?.createPaypalOrder?.id) {
        throw new Error('Failed to create PayPal order')
      }

      setLoading(false)
      return data.createPaypalOrder.id
    } catch (error: any) {
      setLoading(false)
      throw error
    }
  }

  const handleApprove = async (order: any) => {
    // PayPal webhook will handle the backend callback
    // The backend will process the payment via PayPal webhook
    setShowPaypalModal(false)
    setLoading(false)
    onProcess()
  }

  return (
    <>
      <PaymentIcon
        label={tTiers('payment.paypal')}
        source={
          <Image source={PaypalPNG} style={[{ width: 32, height: 32 }]} />
        }
        loading={loading}
        onPress={handlePaypalClick}
      />
      <PaypalButtonsComponent
        visible={showPaypalModal}
        onDismiss={handleClose}
        onCreateOrder={createPaypalOrder}
        onApprove={handleApprove}
        title={tTiers('payment.paypal')}
      />
    </>
  )
}
